import {useEffect, useRef} from 'react';
import {isAxiosError} from 'axios';
import {type DefaultError, useQuery, type UseQueryOptions, type UseQueryResult} from '@tanstack/react-query';
import {notificationStore} from '~/stores/init';
import {axiosErrorToString} from '~/utils/axios.utils';
import type {UrlQueryKey} from './queryClient';

/** Client (400) errors auto-dismiss; 5xx and other failures stay until Retry or close. */
const CLIENT_ERROR_AUTO_HIDE_MS = 4000;

export type CustomQueryOptions<
    TQueryFnData = unknown,
    TError = DefaultError,
    TData = TQueryFnData,
    TQueryKey extends UrlQueryKey = UrlQueryKey,
> = UseQueryOptions<TQueryFnData, TError, TData, TQueryKey> & {
    /**
     * When true, a failed request shows the global error notification.
     * Pass this on the GET query options: `useCustomQuery({ queryKey, queryFn, showErrorNotification: true })`.
     * Default false.
     */
    showErrorNotification?: boolean;
};

function httpStatus(error: unknown): number | undefined {
    return isAxiosError(error) ? error.response?.status : undefined;
}

function errorAutoHideDuration(status: number): number | null {
    return status === 400 ? CLIENT_ERROR_AUTO_HIDE_MS : null;
}

function queryErrorMessage(error: unknown, status: number): string {
    if (status >= 500) {
        return `${status}: Something went wrong. Please try again.`;
    }
    return `${status}: ${axiosErrorToString(error)}`;
}

/**
 * Same as useQuery with {@link UrlQueryKey} as the default query key type (matches defaultQueryFn).
 * Failed requests show a retry notification when `showErrorNotification` is true.
 */
export function useCustomQuery<
    TQueryFnData = unknown,
    TError = DefaultError,
    TData = TQueryFnData,
    TQueryKey extends UrlQueryKey = UrlQueryKey,
>(
    options: CustomQueryOptions<TQueryFnData, TError, TData, TQueryKey>,
): UseQueryResult<TData, TError> {
    const {showErrorNotification = false, ...queryOptions} = options;
    const query = useQuery(queryOptions);
    const {isError, error, errorUpdatedAt, refetch} = query;
    const queryKeyId = JSON.stringify(queryOptions.queryKey);
    const lastShown = useRef({key: '', at: 0});

    useEffect(() => {
        if (!showErrorNotification) return;

        const id = `query-error:${queryKeyId}`;
        if (isError) {
            const status = httpStatus(error);
            if (status === undefined) return;
            if (lastShown.current.key === queryKeyId && lastShown.current.at === errorUpdatedAt) {
                return;
            }
            lastShown.current = {key: queryKeyId, at: errorUpdatedAt};
            const message = queryErrorMessage(error, status);
            notificationStore.show(message, {
                id,
                severity: 'error',
                actionText: 'Retry',
                onAction: () => {
                    void refetch();
                },
                autoHideDuration: errorAutoHideDuration(status),
            });
            return;
        }

        lastShown.current = {key: queryKeyId, at: 0};
        notificationStore.hide(id);
    }, [showErrorNotification, isError, error, errorUpdatedAt, refetch, queryKeyId]);

    return query;
}
